import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

const cards = [
  {
    title: '入住邀请函',
    subtitle: 'INVITATION LETTER',
    image: '/images/onecard_yaoiqng.png',
    route: '#movein',
  },
  {
    title: '房产证',
    subtitle: 'PROPERTY CERTIFICATE',
    image: '/images/onecard_fangchan.png',
    route: '#cert',
  },
  {
    title: '业主卡',
    subtitle: 'OWNER CARD',
    image: '/images/onecard_yezh.png',
    route: '',
  },
  {
    title: '停车位函',
    subtitle: 'PARKING SPACE LETTER',
    image: '/images/onecard_tingche.png',
    route: '',
  },
];

export default function OneCard() {
  const navigate = useNavigate();
  const [certImage, setCertImage] = useState<string | null>(null);
  const [moveinImage, setMoveinImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [highestRoomId, setHighestRoomId] = useState<number | null>(null);
  const [cachedCert, setCachedCert] = useState<string | null>(null);
  const [cachedMovein, setCachedMovein] = useState<string | null>(null);

  useEffect(() => {
    api.get('/property-certificate/check').then(({ data }) => {
      if (Array.isArray(data?.rooms)) {
        const eligible = [...data.rooms].reverse().find((r: any) => r.eligible);
        if (eligible) setHighestRoomId(eligible.room_type_id);
      }
    }).catch(() => {});
  }, []);

  const fetchCerts = async () => {
    if (!highestRoomId) return null;
    if (cachedCert) return { cert: cachedCert, movein: cachedMovein };
    setLoading(true);
    try {
      const { data } = await api.post('/property-certificate/generate', { room_type_id: highestRoomId });
      if (data?.image) setCachedCert(data.image);
      if (data?.movein_image) setCachedMovein(data.movein_image);
      return { cert: data?.image, movein: data?.movein_image };
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (card: typeof cards[0]) => {
    if (card.route === '#cert') {
      if (!highestRoomId) { toast('暂无房产证'); return; }
      const result = await fetchCerts();
      if (result?.cert) setCertImage(result.cert); else toast('暂无房产证');
    } else if (card.route === '#movein') {
      if (!highestRoomId) { toast('暂无入住邀请函'); return; }
      const result = await fetchCerts();
      if (result?.movein) setMoveinImage(result.movein); else toast('暂无入住邀请函');
    } else if (card.route) {
      navigate(card.route);
    } else {
      toast('暂未开放');
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[url(/images/onecard_beijing.png)] bg-[length:100%_100%] bg-no-repeat bg-top relative pt-[270px] ">
      <AuthGuard />

      {/* Back button */}
      <div className="pt-4 px-4 absolute top-0 left-0 w-full flex items-center justify-start">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Cards grid */}
      <div className="px-4 grid grid-cols-2 gap-3 pb-[120px]">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className="overflow-hidden shadow-lg cursor-pointer"
            onClick={() => handleCardClick(card)}
          > 
              <img src={card.image} alt={card.title} className="w-full h-full object-contain" /> 
          </div>
        ))}
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <span className="text-white text-sm">生成中...</span>
        </div>
      )}

      {certImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setCertImage(null)}>
          <div className="relative max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setCertImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-600 shadow-lg text-lg z-10"
            >
              &times;
            </button>
            <img src={certImage} alt="房产证" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}

      {moveinImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setMoveinImage(null)}>
          <div className="relative max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setMoveinImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-600 shadow-lg text-lg z-10"
            >
              &times;
            </button>
            <img src={moveinImage} alt="入住邀请函" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
